/**
 * Admin Experience Editor
 * 
 * Complete admin interface voor Experience Mode management
 */

import React, { useState, useRef } from 'react';
import { ExperienceSpot, SpotLayout, LAYOUT_BREAKPOINTS } from '../../types/experienceMode.types';
import { experienceModeService } from '../../services/experienceModeService';

interface AdminExperienceEditorProps {
  pageId: string;
  spots: ExperienceSpot[];
  onClose: () => void;
  onUpdate: () => void;
}

interface EditingSpot {
  id: string;
  title: string;
  description: string;
  videoUrl: string;
}

export const AdminExperienceEditor: React.FC<AdminExperienceEditorProps> = ({
  pageId,
  spots,
  onClose,
  onUpdate,
}) => {
  const [selectedSpotId, setSelectedSpotId] = useState<string | null>(null);
  const [isPositioning, setIsPositioning] = useState(false);
  const [cursorPosition, setCursorPosition] = useState<{ x: number; y: number } | null>(null);
  const [editingSpot, setEditingSpot] = useState<EditingSpot | null>(null);
  const [uploadingVideo, setUploadingVideo] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Layout selector for multi-breakpoint positioning
  const [activeLayout, setActiveLayout] = useState<SpotLayout>(() => {
    const w = window.innerWidth;
    if (w < LAYOUT_BREAKPOINTS.tablet) return 'mobile';
    if (w < LAYOUT_BREAKPOINTS.desktop) return 'tablet';
    return 'desktop';
  });

  const LAYOUT_LABELS: Record<SpotLayout, { icon: string; label: string }> = {
    desktop: { icon: '🖥️', label: 'Desktop' },
    tablet: { icon: '📱', label: 'Tablet' },
    mobile: { icon: '📱', label: 'Mobiel' },
  };

  // Check if a spot has a position for a given layout
  const hasLayoutPosition = (spot: ExperienceSpot, layout: SpotLayout): boolean => {
    return !!(spot.layoutPositions?.[layout]);
  };

  // Check legacy position
  const hasLegacyPosition = (spot: ExperienceSpot): boolean => {
    return !!(spot.fixedPosition?.x != null && spot.fixedPosition?.y != null);
  };

  // Apply grayscale to page (no backdrop overlay)
  React.useEffect(() => {
    // Hide any experience backdrop elements (we don't need them)
    const backdrops = document.querySelectorAll('.experience-backdrop');
    backdrops.forEach(backdrop => {
      (backdrop as HTMLElement).style.display = 'none';
    });
    
    // Apply grayscale directly to the page
    const appRoot = document.getElementById('app-root');
    
    if (appRoot) {
      appRoot.style.filter = 'grayscale(100%)';
      appRoot.style.transition = 'filter 0.3s ease';
    }
    
    // Cleanup on unmount - remove grayscale
    return () => {
      backdrops.forEach(backdrop => {
        (backdrop as HTMLElement).style.display = '';
      });
      
      if (appRoot) {
        appRoot.style.filter = '';
        appRoot.style.transition = '';
      }
    };
  }, []);

  // Handle mouse movement for positioning
  React.useEffect(() => {
    if (!isPositioning) return;

    const handleMouseMove = (e: MouseEvent) => {
      setCursorPosition({ x: e.clientX, y: e.clientY });
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        cancelPositioning();
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isPositioning]);

  // Place icon on click — saves position for the active layout
  const handlePageClick = async (e: React.MouseEvent) => {
    if (!isPositioning || !selectedSpotId || !cursorPosition) return;

    setIsSaving(true);
    try {
      // Convert viewport click position to document-relative position
      const docX = cursorPosition.x + window.scrollX;
      const docY = cursorPosition.y + window.scrollY;
      const docW = document.documentElement.scrollWidth;
      const docH = document.documentElement.scrollHeight;

      // Get existing spot to preserve other layout positions
      const existingSpot = spots.find(s => s.id === selectedSpotId);
      const existingLayoutPositions = existingSpot?.layoutPositions || {};

      // Save position for the active layout
      const updatedLayoutPositions = {
        ...existingLayoutPositions,
        [activeLayout]: {
          x: docX,
          y: docY,
          refWidth: docW,
          refHeight: docH,
        },
      };

      // Also update legacy fixedPosition for backward compatibility
      await experienceModeService.updateSpot(selectedSpotId, {
        layoutPositions: updatedLayoutPositions,
        fixedPosition: {
          x: docX,
          y: docY,
          xPercent: (docX / docW) * 100,
          yPercent: (docY / docH) * 100,
          refWidth: docW,
          refHeight: docH,
        },
      });
      setIsPositioning(false);
      setSelectedSpotId(null);
      onUpdate();
    } catch (error) {
      console.error('Error saving position:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Start positioning mode
  const startPositioning = (spotId: string) => {
    setSelectedSpotId(spotId);
    setIsPositioning(true);
  };

  // Cancel positioning
  const cancelPositioning = () => {
    setIsPositioning(false);
    setSelectedSpotId(null);
  };

  // Auto-renumber spots by Y position (top to bottom)
  const handleRenumber = async () => {
    if (spots.length === 0) return;
    
    // Sort spots by Y position (top to bottom), then X (left to right)
    const spotsWithPosition = spots.filter(s => 
      s.fixedPosition || s.layoutPositions?.desktop || s.layoutPositions?.tablet || s.layoutPositions?.mobile
    );
    const spotsWithoutPosition = spots.filter(s => 
      !s.fixedPosition && !s.layoutPositions?.desktop && !s.layoutPositions?.tablet && !s.layoutPositions?.mobile
    );
    
    const getY = (s: ExperienceSpot): number => {
      if (s.layoutPositions?.desktop) return s.layoutPositions.desktop.y;
      if (s.layoutPositions?.tablet) return s.layoutPositions.tablet.y;
      if (s.layoutPositions?.mobile) return s.layoutPositions.mobile.y;
      if (s.fixedPosition) return s.fixedPosition.y;
      return 99999;
    };
    const getX = (s: ExperienceSpot): number => {
      if (s.layoutPositions?.desktop) return s.layoutPositions.desktop.x;
      if (s.layoutPositions?.tablet) return s.layoutPositions.tablet.x;
      if (s.layoutPositions?.mobile) return s.layoutPositions.mobile.x;
      if (s.fixedPosition) return s.fixedPosition.x;
      return 99999;
    };
    
    // Sort: first by Y (top to bottom), then by X (left to right)
    const sorted = [...spotsWithPosition].sort((a, b) => {
      const yDiff = getY(a) - getY(b);
      if (Math.abs(yDiff) < 30) return getX(a) - getX(b); // Same row threshold
      return yDiff;
    });
    
    // Add unpositioned spots at the end
    const allSorted = [...sorted, ...spotsWithoutPosition];
    
    setIsSaving(true);
    try {
      const updates = allSorted.map((spot, index) => 
        experienceModeService.updateSpot(spot.id, {
          displayNumber: index + 1,
          order: index + 1,
        })
      );
      await Promise.all(updates);
      onUpdate();
    } catch (error: any) {
      console.error('Error renumbering spots:', error);
      alert(`Fout bij hernummeren: ${error.message || 'Onbekende fout'}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Add new spot
  const handleAddSpot = async () => {
    const nextNumber = spots.length > 0 
      ? Math.max(...spots.map(s => s.displayNumber)) + 1 
      : 1;
    
    setIsSaving(true);
    try {
      console.log('Creating new spot with data:', {
        pageId,
        displayNumber: nextNumber,
        title: `Video ${nextNumber}`,
      });
      
      const spotId = await experienceModeService.createSpot({
        pageId,
        displayNumber: nextNumber,
        title: `Video ${nextNumber}`,
        description: '',
        videoUrl: '',
        order: nextNumber,
        isRequired: false,
      });
      
      console.log('Spot created successfully with ID:', spotId);
      onUpdate();
    } catch (error: any) {
      console.error('Error adding spot:', error);
      alert(`Fout bij toevoegen spot: ${error.message || 'Onbekende fout'}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Delete spot
  const handleDeleteSpot = async (spotId: string) => {
    if (!window.confirm('Weet je zeker dat je deze spot wilt verwijderen?')) return;
    
    setIsSaving(true);
    try {
      await experienceModeService.deleteSpot(spotId);
      onUpdate();
    } catch (error) {
      console.error('Error deleting spot:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Start editing
  const startEditing = (spot: ExperienceSpot) => {
    setEditingSpot({
      id: spot.id,
      title: spot.title,
      description: spot.description,
      videoUrl: spot.videoUrl,
    });
  };

  // Save edits
  const saveEdits = async () => {
    if (!editingSpot) return;
    
    setIsSaving(true);
    try {
      await experienceModeService.updateSpot(editingSpot.id, {
        title: editingSpot.title,
        description: editingSpot.description,
      });
      setEditingSpot(null);
      onUpdate();
    } catch (error) {
      console.error('Error saving edits:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle video upload
  const handleVideoUpload = async (spotId: string, file: File) => {
    // Validate file
    if (!file.type.startsWith('video/')) {
      alert('Please select a valid video file');
      return;
    }

    // Check file size (max 100MB)
    const maxSize = 100 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('Video is te groot. Maximum grootte is 100MB');
      return;
    }

    setUploadingVideo(spotId);
    try {
      console.log('Uploading video:', file.name, 'for spot:', spotId);
      const videoUrl = await experienceModeService.uploadVideo(file, spotId);
      console.log('Video uploaded successfully:', videoUrl);
      
      await experienceModeService.updateSpot(spotId, { videoUrl });
      console.log('Spot updated with video URL');
      
      onUpdate();
    } catch (error: any) {
      console.error('Error uploading video:', error);
      alert(`Fout bij uploaden video: ${error.message || 'Onbekende fout'}`);
    } finally {
      setUploadingVideo(null);
    }
  };

  // Trigger file input
  const triggerFileUpload = (spotId: string) => {
    setSelectedSpotId(spotId);
    fileInputRef.current?.click();
  };

  return (
    <div className="admin-experience-editor">
      {/* Top Banner */}
      <div className="editor-banner">
        <div className="banner-content">
          <span className="text-2xl">🎯</span>
          <div className="flex-1">
            <h3 className="font-bold text-white mb-1">
              Experience Mode Editor - {pageId}
            </h3>
            <p className="text-sm text-white/90">
              {isPositioning 
                ? `Klik om spot te plaatsen voor ${LAYOUT_LABELS[activeLayout].label} (ESC = annuleren)` 
                : 'Beheer video spots — kies een layout en plaats per schermgrootte'}
            </p>
          </div>
          
          {/* Layout Selector */}
          <div className="layout-selector">
            {(Object.keys(LAYOUT_LABELS) as SpotLayout[]).map(layout => (
              <button
                key={layout}
                className={`layout-btn ${activeLayout === layout ? 'active' : ''}`}
                onClick={() => setActiveLayout(layout)}
                title={LAYOUT_LABELS[layout].label}
              >
                <span>{LAYOUT_LABELS[layout].icon}</span>
                <span className="layout-btn-label">{LAYOUT_LABELS[layout].label}</span>
              </button>
            ))}
          </div>
          
          <button onClick={onClose} className="banner-close-btn">
            Sluiten
          </button>
        </div>
      </div>

      {/* Positioning Overlay */}
      {isPositioning && (
        <>
          <div className="positioning-overlay" onClick={handlePageClick}>
            {cursorPosition && (() => {
              const activeSpot = spots.find(s => s.id === selectedSpotId);
              return (
                <div
                  className="cursor-preview"
                  style={{ left: `${cursorPosition.x}px`, top: `${cursorPosition.y}px` }}
                >
                  <div className="cursor-spot-label">
                    <div className="cursor-spot-indicator">
                      <span>{activeSpot?.displayNumber}</span>
                    </div>
                    <div className="cursor-spot-text">
                      <span className="cursor-spot-title">{activeSpot?.title || 'Video'}</span>
                      {activeSpot?.description && (
                        <span className="cursor-spot-desc">{activeSpot.description}</span>
                      )}
                    </div>
                  </div>
                  <div className="cursor-hint">Klik om te plaatsen</div>
                </div>
              );
            })()}
          </div>
          <button className="cancel-positioning-btn" onClick={cancelPositioning}>
            ✕ Annuleren (ESC)
          </button>
        </>
      )}

      {/* Spots Panel - Hide during positioning */}
      {!isPositioning && (
        <div className="spots-panel">
          <div className="panel-header">
            <h4 className="panel-title">Video Spots ({spots.length})</h4>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={handleRenumber} disabled={isSaving || spots.length === 0} className="add-spot-btn" style={{ backgroundColor: '#f3f4f6', color: '#374151' }} title="Hernummer spots op volgorde van positie (boven→onder)">
                {isSaving ? '⏳' : '🔢'} Hernummer
              </button>
              <button onClick={handleAddSpot} disabled={isSaving} className="add-spot-btn">
                {isSaving ? '⏳ Bezig...' : '➕ Nieuwe Spot'}
              </button>
            </div>
          </div>

          <div className="spots-grid">
          {spots.map(spot => (
            <div key={spot.id} className="spot-card">
              {/* Card Header */}
              <div className="card-header">
                <span className="spot-number">{spot.displayNumber}</span>
                <div className="card-actions">
                  <button
                    onClick={() => startPositioning(spot.id)}
                    className="action-btn position"
                    title="Plaats icoon"
                    disabled={isSaving}
                  >
                    📍
                  </button>
                  <button
                    onClick={() => handleDeleteSpot(spot.id)}
                    className="action-btn delete"
                    title="Verwijderen"
                    disabled={isSaving}
                  >
                    🗑️
                  </button>
                </div>
              </div>

              {/* Video Section */}
              <div className="card-video">
                {spot.videoUrl ? (
                  <div className="video-preview">
                    <video src={spot.videoUrl} className="preview-video" />
                    <div className="video-overlay">
                      <button
                        onClick={() => triggerFileUpload(spot.id)}
                        className="change-video-btn"
                        disabled={uploadingVideo === spot.id}
                      >
                        {uploadingVideo === spot.id ? '⏳' : '🎥'} Wijzig Video
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="video-placeholder">
                    <button
                      onClick={() => triggerFileUpload(spot.id)}
                      className="upload-video-btn"
                      disabled={uploadingVideo === spot.id}
                    >
                      {uploadingVideo === spot.id ? (
                        <>
                          <div className="spinner" />
                          <span>Uploaden...</span>
                        </>
                      ) : (
                        <>
                          <span className="text-3xl">📹</span>
                          <span>Upload Video</span>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>

              {/* Content Section */}
              <div className="card-content">
                {editingSpot?.id === spot.id ? (
                  <>
                    <input
                      type="text"
                      value={editingSpot.title}
                      onChange={e => setEditingSpot({ ...editingSpot, title: e.target.value })}
                      className="edit-input title"
                      placeholder="Titel"
                    />
                    <textarea
                      value={editingSpot.description}
                      onChange={e => setEditingSpot({ ...editingSpot, description: e.target.value })}
                      className="edit-input description"
                      placeholder="Beschrijving (optioneel)"
                      rows={2}
                    />
                    <div className="edit-actions">
                      <button onClick={saveEdits} className="save-btn" disabled={isSaving}>
                        ✓ Opslaan
                      </button>
                      <button onClick={() => setEditingSpot(null)} className="cancel-btn">
                        ✕ Annuleren
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <h5 className="card-title" onClick={() => startEditing(spot)}>
                      {spot.title || 'Geen titel'}
                      <span className="edit-icon">✏️</span>
                    </h5>
                    <p className="card-description" onClick={() => startEditing(spot)}>
                      {spot.description || 'Klik om beschrijving toe te voegen'}
                    </p>
                  </>
                )}
              </div>

              {/* Layout Status Indicators */}
              <div className="card-footer">
                <div className="layout-status-row">
                  {(Object.keys(LAYOUT_LABELS) as SpotLayout[]).map(layout => {
                    const hasPos = hasLayoutPosition(spot, layout);
                    const isActive = layout === activeLayout;
                    return (
                      <span 
                        key={layout} 
                        className={`layout-status ${hasPos ? 'placed' : 'missing'} ${isActive ? 'active-layout' : ''}`}
                        title={`${LAYOUT_LABELS[layout].label}: ${hasPos ? 'Geplaatst' : 'Nog niet geplaatst'}`}
                      >
                        {hasPos ? '✓' : '—'} {LAYOUT_LABELS[layout].icon}
                      </span>
                    );
                  })}
                  {/* Legacy fallback indicator */}
                  {hasLegacyPosition(spot) && !spot.layoutPositions && (
                    <span className="status legacy" title="Legacy positie (enkel desktop)">
                      📌 Legacy
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        style={{ display: 'none' }}
        onChange={e => {
          const file = e.target.files?.[0];
          if (file && selectedSpotId) {
            handleVideoUpload(selectedSpotId, file);
          }
          e.target.value = ''; // Reset input
        }}
      />

      <style>{`
        .admin-experience-editor {
          position: fixed;
          inset: 0;
          z-index: 9999;
          pointer-events: none;
        }

        .editor-banner {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
          z-index: 10001;
          pointer-events: auto;
        }

        .banner-content {
          max-width: 1400px;
          margin: 0 auto;
          padding: 1rem 1.5rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .banner-close-btn {
          padding: 0.5rem 1rem;
          background: white;
          color: #3b82f6;
          border: none;
          border-radius: 0.5rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          flex-shrink: 0;
        }

        .banner-close-btn:hover {
          background: #f3f4f6;
          transform: scale(1.05);
        }

        /* ─── Mobile-responsive banner ─── */
        @media (max-width: 768px) {
          .banner-content {
            padding: 0.75rem;
            gap: 0.5rem;
          }

          .banner-content > .text-2xl {
            display: none;
          }

          .banner-content > .flex-1 h3 {
            font-size: 0.875rem;
            margin-bottom: 0;
          }

          .banner-content > .flex-1 p {
            display: none;
          }

          .layout-selector {
            order: 10;
            width: 100%;
            justify-content: center;
          }

          .layout-btn {
            flex: 1;
            justify-content: center;
            padding: 8px 6px;
          }

          .layout-btn-label {
            font-size: 11px;
          }

          .banner-close-btn {
            padding: 0.4rem 0.75rem;
            font-size: 0.8rem;
          }
        }

        .positioning-overlay {
          position: fixed;
          inset: 0;
          cursor: crosshair;
          z-index: 10000;
          pointer-events: auto;
          background: transparent;
        }

        .cursor-preview {
          position: fixed;
          pointer-events: none;
          transform: translate(-6px, -50%);
          z-index: 10002;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 6px;
        }

        .cursor-spot-label {
          display: flex;
          align-items: center;
          gap: 10px;
          background: rgba(255, 255, 255, 0.95);
          border: 2px solid #10b981;
          border-radius: 8px;
          padding: 6px 14px 6px 6px;
          box-shadow: 0 4px 16px rgba(16, 185, 129, 0.25);
          animation: cursorPulse 1.5s ease-in-out infinite;
        }

        @keyframes cursorPulse {
          0%, 100% { box-shadow: 0 4px 16px rgba(16, 185, 129, 0.25); }
          50% { box-shadow: 0 4px 24px rgba(16, 185, 129, 0.45); }
        }

        .cursor-spot-indicator {
          width: 28px;
          height: 28px;
          background: #10b981;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 13px;
          font-weight: 700;
          flex-shrink: 0;
        }

        .cursor-spot-text {
          display: flex;
          flex-direction: column;
          gap: 1px;
        }

        .cursor-spot-title {
          font-size: 13px;
          font-weight: 600;
          color: #1f2937;
          white-space: nowrap;
        }

        .cursor-spot-desc {
          font-size: 11px;
          font-weight: 400;
          color: #6b7280;
          white-space: nowrap;
        }

        .cursor-hint {
          background: rgba(0, 0, 0, 0.75);
          color: white;
          padding: 3px 10px;
          border-radius: 4px;
          font-size: 11px;
          white-space: nowrap;
        }

        .cancel-positioning-btn {
          position: fixed;
          top: 90px;
          left: 50%;
          transform: translateX(-50%);
          background: #ef4444;
          color: white;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 0.75rem;
          font-weight: 600;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
          z-index: 10001;
          transition: all 0.2s;
          pointer-events: auto;
        }

        .cancel-positioning-btn:hover {
          background: #dc2626;
          transform: translateX(-50%) scale(1.05);
        }

        @media (max-width: 768px) {
          .cancel-positioning-btn {
            top: 80px;
            padding: 0.5rem 1rem;
            font-size: 0.85rem;
          }
        }

        .spots-panel {
          position: fixed;
          top: 100px;
          left: 50%;
          transform: translateX(-50%);
          width: 90%;
          max-width: 1400px;
          max-height: calc(100vh - 120px);
          background: white;
          border-radius: 1rem;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
          overflow: hidden;
          display: flex;
          flex-direction: column;
          z-index: 10000;
          pointer-events: auto;
        }

        @media (max-width: 768px) {
          .spots-panel {
            top: 90px;
            width: 96%;
            max-height: calc(100vh - 100px);
            border-radius: 0.75rem;
          }

          .panel-header {
            padding: 0.75rem;
            flex-wrap: wrap;
            gap: 0.5rem;
          }

          .panel-title {
            font-size: 1rem;
          }

          .add-spot-btn {
            padding: 0.5rem 1rem;
            font-size: 0.8rem;
          }

          .spots-grid {
            padding: 0.75rem;
            grid-template-columns: 1fr;
            gap: 1rem;
            max-height: calc(100vh - 220px);
          }

          .card-header {
            padding: 0.75rem;
          }

          .card-content {
            padding: 0.75rem;
          }

          .card-footer {
            padding: 0.5rem 0.75rem;
          }
        }

        .panel-header {
          padding: 1.5rem;
          border-bottom: 2px solid #e5e7eb;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .panel-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1f2937;
        }

        .add-spot-btn {
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          border: none;
          border-radius: 0.5rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .add-spot-btn:hover {
          transform: scale(1.05);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
        }

        .add-spot-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .spots-grid {
          padding: 1.5rem;
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 1.5rem;
          overflow-y: auto;
          max-height: calc(100vh - 240px);
        }

        .spot-card {
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          overflow: hidden;
          transition: all 0.2s;
        }

        .spot-card:hover {
          border-color: #3b82f6;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
        }

        .card-header {
          padding: 1rem;
          background: #f9fafb;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid #e5e7eb;
        }

        .spot-number {
          width: 32px;
          height: 32px;
          background: #10b981;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
        }

        .card-actions {
          display: flex;
          gap: 0.5rem;
        }

        .action-btn {
          width: 32px;
          height: 32px;
          border: none;
          border-radius: 0.5rem;
          cursor: pointer;
          font-size: 16px;
          transition: all 0.2s;
        }

        .action-btn.position {
          background: #dbeafe;
        }

        .action-btn.position:hover {
          background: #3b82f6;
          transform: scale(1.1);
        }

        .action-btn.delete {
          background: #fee2e2;
        }

        .action-btn.delete:hover {
          background: #ef4444;
          transform: scale(1.1);
        }

        .action-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .card-video {
          aspect-ratio: 16/9;
          background: #000;
          position: relative;
        }

        .video-preview {
          width: 100%;
          height: 100%;
          position: relative;
        }

        .preview-video {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .video-overlay {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.2s;
        }

        .video-preview:hover .video-overlay {
          opacity: 1;
        }

        .change-video-btn {
          padding: 0.75rem 1.5rem;
          background: white;
          border: none;
          border-radius: 0.5rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .change-video-btn:hover {
          transform: scale(1.05);
        }

        .video-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
        }

        .upload-video-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
          padding: 2rem;
          background: transparent;
          border: 2px dashed rgba(255, 255, 255, 0.3);
          border-radius: 0.75rem;
          color: white;
          cursor: pointer;
          transition: all 0.2s;
        }

        .upload-video-btn:hover {
          border-color: #10b981;
          background: rgba(16, 185, 129, 0.1);
        }

        .spinner {
          width: 32px;
          height: 32px;
          border: 3px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .card-content {
          padding: 1rem;
        }

        .card-title {
          font-size: 1rem;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 0.5rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .edit-icon {
          opacity: 0;
          transition: opacity 0.2s;
        }

        .card-title:hover .edit-icon {
          opacity: 1;
        }

        .card-description {
          font-size: 0.875rem;
          color: #6b7280;
          line-height: 1.5;
          cursor: pointer;
        }

        .card-description:empty::before {
          content: 'Klik om beschrijving toe te voegen';
          color: #9ca3af;
          font-style: italic;
        }

        .edit-input {
          width: 100%;
          padding: 0.5rem;
          border: 2px solid #3b82f6;
          border-radius: 0.375rem;
          font-family: inherit;
        }

        .edit-input.title {
          font-weight: 600;
          font-size: 1rem;
          margin-bottom: 0.5rem;
        }

        .edit-input.description {
          font-size: 0.875rem;
          resize: vertical;
        }

        .edit-actions {
          margin-top: 0.75rem;
          display: flex;
          gap: 0.5rem;
        }

        .save-btn {
          flex: 1;
          padding: 0.5rem;
          background: #10b981;
          color: white;
          border: none;
          border-radius: 0.375rem;
          font-weight: 600;
          cursor: pointer;
        }

        .save-btn:hover {
          background: #059669;
        }

        .cancel-btn {
          padding: 0.5rem 1rem;
          background: #e5e7eb;
          border: none;
          border-radius: 0.375rem;
          cursor: pointer;
        }

        .cancel-btn:hover {
          background: #d1d5db;
        }

        .card-footer {
          padding: 0.75rem 1rem;
          background: #f9fafb;
          border-top: 1px solid #e5e7eb;
        }

        .status {
          font-size: 0.75rem;
          font-weight: 600;
          padding: 0.25rem 0.75rem;
          border-radius: 0.375rem;
          display: inline-block;
        }

        .status.placed {
          background: #d1fae5;
          color: #065f46;
        }

        .status.not-placed {
          background: #fed7aa;
          color: #9a3412;
        }

        /* Layout Selector in Banner */
        .layout-selector {
          display: flex;
          gap: 4px;
          background: rgba(255, 255, 255, 0.15);
          border-radius: 0.5rem;
          padding: 3px;
        }

        .layout-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border: none;
          border-radius: 0.375rem;
          background: transparent;
          color: rgba(255, 255, 255, 0.7);
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
        }

        .layout-btn:hover {
          background: rgba(255, 255, 255, 0.15);
          color: white;
        }

        .layout-btn.active {
          background: white;
          color: #2563eb;
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
        }

        .layout-btn-label {
          font-size: 12px;
        }

        /* Layout Status Row in Card Footer */
        .layout-status-row {
          display: flex;
          gap: 8px;
          align-items: center;
          flex-wrap: wrap;
        }

        .layout-status {
          font-size: 0.7rem;
          font-weight: 600;
          padding: 2px 8px;
          border-radius: 0.375rem;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          border: 1.5px solid transparent;
        }

        .layout-status.placed {
          background: #d1fae5;
          color: #065f46;
        }

        .layout-status.missing {
          background: #f3f4f6;
          color: #9ca3af;
        }

        .layout-status.active-layout {
          border-color: #3b82f6;
          box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.3);
        }

        .status.legacy {
          background: #fef3c7;
          color: #92400e;
          font-size: 0.7rem;
          font-weight: 600;
          padding: 2px 8px;
          border-radius: 0.375rem;
        }
      `}</style>
    </div>
  );
};
